import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faStop } from '@fortawesome/free-solid-svg-icons';
import './DrumMachine.css';
import * as Tone from 'tone';

const drumSoundPaths = {
  kick: '/sounds/kick.wav',
  snare: '/sounds/snare.wav',
  hihat: '/sounds/hihat.wav',
};

const initialPattern = {
  kick: [1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0],
  snare: [0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0],
  hihat: [1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0],
};

function DrumMachine() {
  const drumPlayersRef = useRef(null);
  const sequenceRef = useRef(null);
  const patternRef = useRef(initialPattern);
  const lastStartTimeRef = useRef(0);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [tempo, setTempo] = useState(120);
  const [currentStep, setCurrentStep] = useState(0);
  const [pattern, setPattern] = useState(initialPattern);

  useEffect(() => {
	patternRef.current = pattern;
  }, [pattern]);

  useEffect(() => {
	const initTone = async () => {
	  try {
		if (drumPlayersRef.current && sequenceRef.current) {
		  setIsLoading(false);
		  return;
		}

		drumPlayersRef.current = new Tone.Players(
		  {
			kick: drumSoundPaths.kick,
			snare: drumSoundPaths.snare,
			hihat: drumSoundPaths.hihat,
		  },
		  () => {
			setIsLoading(false);
		  }
		).toDestination();

		// Wait for all audio buffers to load
		await drumPlayersRef.current.loaded;

		// Create sequence
		sequenceRef.current = new Tone.Sequence(
		  (time, step) => {
			Object.keys(patternRef.current).forEach((drumType) => {
			  if (patternRef.current[drumType][step] === 1) {
				let scheduledTime = time;

				if (scheduledTime <= lastStartTimeRef.current) {
				  scheduledTime = lastStartTimeRef.current + 0.001; // delay by 1ms
				}

				// Check if the buffer is loaded before playing
				const player = drumPlayersRef.current.player(drumType);
				if (player && player.buffer && player.buffer.loaded) {
				  player.start(scheduledTime);
				  lastStartTimeRef.current = scheduledTime;
				} else {
				  console.warn(`Buffer for ${drumType} is not loaded yet.`);
				}
			  }
			});

			setCurrentStep(step);
		  },
		  [...Array(16).keys()],
		  "16n"
		);

		sequenceRef.current.start(0);
		Tone.Transport.bpm.value = tempo;
		Tone.Transport.loop = true;
		Tone.Transport.loopEnd = "1n";

		setError(null);
	  } catch (err) {
		setError(`Failed to load audio or initialize Tone.js: ${err.message}`);
		setIsLoading(false);
	  }
	};

	initTone();

	return () => {
	  if (sequenceRef.current) {
		sequenceRef.current.stop();
		sequenceRef.current.dispose();
		sequenceRef.current = null;
	  }
	  if (drumPlayersRef.current) {
		drumPlayersRef.current.dispose();
		drumPlayersRef.current = null;
	  }
	  Tone.Transport.stop();
	  Tone.Transport.cancel();
	};
  }, []);

  useEffect(() => {
	if (!isLoading && Tone.Transport) {
	  Tone.Transport.bpm.value = tempo;
	}
  }, [tempo, isLoading]);

  useEffect(() => {
	if (!isLoading && !error && Tone.Transport) {
	  if (isPlaying) {
		Tone.start().then(() => {
		  Tone.Transport.start();
		}).catch((err) => {
		  setError("Autoplay prevented. Please interact with page. " + err.message);
		  setIsPlaying(false);
		});
	  } else {
		Tone.Transport.stop();
		setCurrentStep(0);
	  }
	}
  }, [isPlaying, isLoading, error]);

  const toggleStep = useCallback((drumType, stepIndex) => {
	setPattern(prev => ({
	  ...prev,
	  [drumType]: prev[drumType].map((v, i) => (i === stepIndex ? (v === 1 ? 0 : 1) : v))
	}));
  }, []);

  if (isLoading) {
	return (
	  <div className="react-drum-machine-container app-loading-fallback">
		<div className="spinner"></div>
		<p>Loading drum sounds...</p>
	  </div>
	);
  }

  if (error) {
	return (
	  <div className="react-drum-machine-container error-state">
		<p className="error-message">{error}</p>
		<button onClick={() => window.location.reload()} className="play-stop-button" style={{ marginTop: '1rem' }}>
		  Reload Page
		</button>
	  </div>
	);
  }

  return (
	<div className="react-drum-machine-container">
	  <h2>808 Drum Machine</h2>

	  <div className="controls-section">
		<button 
		  onClick={() => setIsPlaying(!isPlaying)} 
		  className={`play-stop-button ${isPlaying ? 'playing' : ''}`}
		  disabled={isLoading || error}
		>
		  <FontAwesomeIcon icon={isPlaying ? faStop : faPlay} />
		  {isPlaying ? ' Stop' : ' Play'}
		</button>

		<div className="tempo-control">
		  <label htmlFor="tempo-slider">Tempo:</label>
		  <input
			type="range"
			id="tempo-slider"
			min="60"
			max="200"
			step="1"
			value={tempo}
			onChange={(e) => setTempo(parseInt(e.target.value))}
			className="range-input"
		  />
		  <span className="tempo-value">{tempo} BPM</span>
		</div>
	  </div>

	  <div className="sequencer-grid">
		<div className="drum-label"></div>
		{Array.from({ length: 16 }).map((_, idx) => (
		  <div 
			key={`step-header-${idx}`} 
			className={`step-header ${idx === currentStep ? 'current' : ''}`}
		  >
			{idx + 1}
		  </div>
		))}

		{Object.keys(pattern).map(drumType => (
		  <React.Fragment key={drumType}>
			<div className="drum-label">{drumType.charAt(0).toUpperCase() + drumType.slice(1)}</div>
			{pattern[drumType].map((value, idx) => (
			  <button
				key={`${drumType}-${idx}`}
				className={`step-button ${value === 1 ? 'active' : ''} ${idx === currentStep ? 'current' : ''}`}
				onClick={() => toggleStep(drumType, idx)}
			  />
			))}
		  </React.Fragment>
		))}
	  </div>
	</div>
  );
}

export default DrumMachine;
